import { CustomStepper } from '../../../components/CustomStepper';
import { AccountDetails } from './AccountDetails';
import { useOperator } from '../../../contexts/OperatorContext';
import { FormEvent, useEffect, useState } from 'react';
import { Packages } from './Packages';
import { Devices } from './Devices';
import { Review } from './Review';
import { isValidIpAddress, isValidPhoneNumber } from '../../../lib/validators';
import { useCreateAccount } from '../../../hooks/Account/useCreateAccount';
import { CreateAccountLoading } from './CreateAccountLoading';
import { NoDataMessage } from '../../../components/NoDataMessage';
import { WarningOutlined } from '@material-ui/icons';

export const CreateAccount = () => {
  const {
    accountExists,
    errors,
    formActive,
    formValues,
    inventory,
    inventoryLoading,
    onFormSubmit,
    optionMenus,
    resetState,
    progress,
    updateField,
    updateFormState,
  } = useCreateAccount();
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const mainScroller = document.getElementById('main-scroller');
  const operator = useOperator();

  useEffect(() => {
    setCreating(false);
    setReviewed(false);
  }, [operator.id]);

  const validateIpAddress = (): boolean => {
    if (!formValues.ipAddress && formValues.startIpAddress === undefined)
      return isValidIpAddress(formValues.ipAddress ?? '');

    return (
      isValidIpAddress(formValues.startIpAddress) &&
      formValues.startIpAddress !== '' &&
      isValidIpAddress(formValues.ipAddress) &&
      formValues.ipAddress !== ''
    );
  };

  const accountDetailsValid: boolean =
    formValues.accountNumber.trim() !== '' &&
    formValues.fipsCode !== '' &&
    formValues.transportTypeName !== '' &&
    (!formValues.phoneNumber
      ? true
      : isValidPhoneNumber(formValues.phoneNumber)) &&
    validateIpAddress();

  const packagesValid = formValues.basePackage !== '';

  const hasAlaCarte =
    (operator.packages ?? [])
      .filter((p) => p.active)
      .find((p) => p.type === 'alaCarte') !== undefined;

  const handleFormChange = (value: any, name: string, step: number) => {
    setCurrentStep(step);
    updateField(name as keyof CreateAccount, value);
  };

  const steps: StepDefinition[] = [
    {
      id: 1,
      description:
        'Some fields have been pre-selected with operator defaults. Please review all fields before proceeding.',
      label: 'Account Details',
      content: (
        <AccountDetails
          formActive={formActive}
          formValues={formValues}
          onChange={(value, name) => handleFormChange(value, name, 0)}
          optionMenus={optionMenus}
          updateFormState={updateFormState}
        />
      ),
      validation: accountDetailsValid,
    },
    {
      id: 2,
      description: `Add-on ${
        hasAlaCarte ? 'and À La Carte ' : ''
      }packages can be added to the account at a later time`,
      label: 'Packages',
      content: (
        <Packages
          formActive={formActive}
          formValues={formValues}
          optionMenus={optionMenus}
          updateFormState={updateFormState}
        />
      ),
      validation: formValues.basePackage !== '',
    },
    {
      id: 3,
      description:
        'This is an optional step. Devices can be added to the account at a later time.',
      label: 'Devices',
      content: (
        <Devices
          formActive={formActive}
          formValues={formValues}
          inventory={inventory}
          loading={inventoryLoading}
          updateFormState={updateFormState}
        />
      ),
      validation: true,
    },
    {
      id: 4,
      description: '*Accounts cannot be deleted once created.',
      label: 'Review & Create',
      content: (
        <Review
          canCreateAccount={accountDetailsValid && packagesValid}
          formActive={formActive}
          formValues={formValues}
          onChange={(value, name) => handleFormChange(value, name, 3)}
          onReviewed={setReviewed}
          optionMenus={optionMenus}
          reviewed={reviewed}
          updateFormState={updateFormState}
        />
      ),
      validation: reviewed && accountDetailsValid && packagesValid,
    },
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (mainScroller) mainScroller.scrollTo({ top: 0, left: 0 });
    setCreating(true);
    onFormSubmit(event);
  };

  if (
    !(operator.transports ?? []).some((t) => t.active) ||
    !(operator.fips ?? []).some((f) => f.active)
  ) {
    return (
      <div className="p-12">
        <NoDataMessage
          icon={<WarningOutlined fontSize="large" />}
          title="Operator Setup Required"
          subTitle="At least 1 active Transport and FIPS code must be configured to create a new account"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      {creating ? (
        <CreateAccountLoading
          account={formValues}
          accountExists={accountExists}
          errors={errors}
          onBack={() => {
            resetState();
            setCreating(false);
            setReviewed(false);
          }}
          progress={progress}
        />
      ) : (
        <CustomStepper
          canSubmit={accountDetailsValid && packagesValid}
          currentStep={currentStep}
          formActive={formActive}
          header="Create Account"
          steps={steps}
          onReset={() => {
            setReviewed(false);
            resetState();
          }}
          onSubmit={handleSubmit}
          submitButton="Create Account"
        />
      )}
    </div>
  );
};
